import { PhoneService } from '@superdispatch/phones';
import { EMPTY_ERROR_MESSAGE } from 'shared/form/FormikMediaField';
import { driverCdlVerificationStatus } from 'shared/modules/driver/DriverCdlVerificationStatus';
import { passwordCheckTypes } from 'shared/utils/PasswordUtils';
import { z } from 'zod';

const userStatus = [
  'activated',
  'deactivated',
  'pending',
  'invited',
  'join_requested',
  'activated,invited',
] as const;
const userType = ['invite', 'join_request'] as const;
const userRole = [
  'driver',
  'office_staff',
  'admin',
  'accounting',
  'operations',
  'dispatching',
] as const;

const usersPageFilterStatus = [...userStatus, 'all'] as const;

export type UserRole = (typeof userRole)[number];

export type UserDTO = z.infer<typeof userSchema>;
export const userSchema = z.object({
  name: z.string().default(''),
  email: z.string().default(''),
  phone_number: z.string().default(''),
  status: z.enum(userStatus).default('activated'),
  guid: z.string().default(''),
  type: z.enum(userType).default('invite'),
  roles: z.array(z.string()).default([]),
  internal_name: z.string().default(''),
  internal_notes: z.string().default(''),
  is_myself: z.boolean().nullable().default(false),
  is_cdl_verification_requested: z.boolean().nullable().default(false),
  // A missing `driver` casts to a default object. Only an explicit `null`
  // marks a non-driver, and `!!user.driver` gates driver UI.
  driver: z
    .object({
      guid: z.string().default(''),
      inbox: z.string().default(''),
      cdl_verification_status: z
        .enum(driverCdlVerificationStatus)
        .nullable()
        .default(null),
      is_cdl_expiring_soon: z.boolean().nullable().default(false),
      trailer: z
        .object({ capacity: z.number().nullable().default(null) })
        .prefault({}),
      drivers_licence: z
        .object({
          number: z.string().default(''),
          state: z.string().default(''),
        })
        .prefault({}),
    })
    .nullable()
    .prefault({}),
  office_staff: z
    .object({ guid: z.string().default('') })
    .nullable()
    .prefault({}),
});

export type UserListParamsDTO = z.infer<typeof userListParamsSchema>;
const userListParamsSchema = z.object({
  page: z.number().nullable().optional(),
  page_size: z.number().nullable().optional(),
  status: z.string().optional(),
  type: z.enum(userType).optional(),
  role: z.enum(userRole).optional(),
  role_in: z.string().optional(),
  ordering: z.enum(['name', '-name']).optional(),
  q: z.string().optional(),
});

export type UsersCountDTO = z.infer<typeof usersCountSchema>;
export const usersCountSchema = z.object({
  all: z.number().nullable().default(null),
  driver: z.number().nullable().default(null),
  office_staff: z.number().nullable().default(null),
  dispatching: z.number().nullable().default(null),
  accounting: z.number().nullable().default(null),
  operations: z.number().nullable().default(null),
  admin: z.number().nullable().default(null),
});

export type UsersPageFiltersDTO = z.infer<typeof usersPageFiltersSchema>;
export const usersPageFiltersSchema = z.object({
  q: z.string().default(''),
  status: z.enum(usersPageFilterStatus).default('all'),
  role: z.enum([...userRole, 'all']).default('all'),
  ordering: z.enum(['name', '-name']).nullable().default(null),
  type: z.enum(userType).nullable().default(null),
});

export function isValidUsersPageFilterStatus(
  value: string,
): value is UsersPageFiltersDTO['status'] {
  return (usersPageFilterStatus as readonly string[]).includes(value);
}

export function getUserName(member: UserDTO): string {
  return member.is_myself
    ? member.name || member.email
    : member.internal_name || member.name || member.email;
}

export const DEACTIVATION_REASON_TYPES = [
  'left_company',
  'temporary_leave',
  'duplicate_or_wrong_person',
  'company_closed',
  'too_expensive',
  'no_value',
  'app_issue',
  'other',
] as const;

export type DeactivationReasonType = (typeof DEACTIVATION_REASON_TYPES)[number];

const deactivationReasonTypeLabels: Record<DeactivationReasonType, string> = {
  left_company: 'User left company',
  temporary_leave: 'Temporary / seasonal leave',
  duplicate_or_wrong_person: 'Duplicate or wrong person',
  company_closed: 'Company closed',
  too_expensive: 'Too expensive',
  no_value: 'Not getting value / loads',
  app_issue: 'App or tech issue',
  other: 'Other',
};

export function formatDeactivationReasonType(
  reasonType: DeactivationReasonType,
): string {
  return deactivationReasonTypeLabels[reasonType];
}

export interface UserDeactivationFormValues {
  reason_type: DeactivationReasonType | null;
  reason: string;
}

export type UserInviteFormDTO = z.infer<typeof userInviteSchema>;
// Formik turns '' into undefined before validating, so `.prefault('')` lets
// the required checks report their message instead of a type error.
export const userInviteSchema = z.object({
  name: z.string().default(''),
  email: z
    .string()
    .min(1, 'Email is required')
    .pipe(z.email('Enter a valid email'))
    .prefault(''),
  phone_number: z.string().min(1, 'Phone number is required').prefault(''),
  roles: z
    .array(z.string())
    .min(1, 'Select at Least One Permission')
    .prefault([]),
});

export const USER_INVITE_FORM_INITIAL_VALUES: UserInviteFormDTO = {
  name: '',
  email: '',
  phone_number: '',
  roles: ['driver'],
};

export type UserInvitationDTO = z.infer<typeof userInvitationSchema>;
export const userInvitationSchema = z.object({
  carrier: z
    .object({
      name: z.string().default(''),
      abbreviation: z.string().default(''),
      address: z.string().default(''),
      city: z.string().default(''),
      state: z.string().default(''),
      zip: z.string().default(''),
      usdot: z.string().default(''),
      logo: z.string().default(''),
    })
    .prefault({}),
  inviter_name: z.string().default(''),
  contact_name: z.string().default(''),
  email: z.string().default(''),
  full_name: z.string().default(''),
  phone_number: z.string().default(''),
  status: z.string().default(''),
  is_new_user: z.boolean().nullable().default(false),
  roles: z.array(z.string()).default([]),
});

export type UserActivateDTO = z.infer<typeof userActivationSchema>;
// Formik turns '' into undefined before validating, so `.prefault('')` lets
// the required checks report their message instead of a type error.
export const userActivationSchema = z.object({
  email: z.string().default(''),
  full_name: z.string().min(1, 'This field is required').prefault(''),
  phone_number: z
    .string()
    .trim()
    .min(1, 'This field is required')
    .superRefine(async (value, ctx) => {
      const message = await PhoneService.validate(value);

      if (message) {
        ctx.addIssue({ code: 'custom', message });
      }
    })
    .prefault(''),
  trailer_capacity: z.number().nullable().default(null),
  is_subscribed: z.boolean().nullable().default(false),
  password: z
    .string()
    .min(8, EMPTY_ERROR_MESSAGE)
    .regex(passwordCheckTypes.toLowerCaseAndUpperCase, EMPTY_ERROR_MESSAGE)
    .regex(passwordCheckTypes.toSpecialCharacter, EMPTY_ERROR_MESSAGE)
    .regex(passwordCheckTypes.toNumber, EMPTY_ERROR_MESSAGE)
    .prefault(''),
});

export type UserInviteDTO = z.infer<typeof invitedUserSchema>;
const invitedUserSchema = z.object({
  name: z.string().default(''),
  email: z.string().default(''),
  full_name: z.string().default(''),
  phone_number: z.string().default(''),
  guid: z.string().default(''),
  is_subscribed: z.boolean().nullable().default(false),
  roles: z.array(z.string()).default([]),
  type: z.string().default(''),
  status: z.string().default(''),
  driver: z
    .object({
      guid: z.string().default(''),
      inbox: z.string().default(''),
      trailer: z
        .object({ capacity: z.number().nullable().default(null) })
        .prefault({}),
      drivers_licence: z
        .object({
          number: z.string().default(''),
          state: z.string().default(''),
        })
        .prefault({}),
    })
    .prefault({}),
  office_staff: z.object({ guid: z.string().default('') }).prefault({}),
  internal_notes: z.string().default(''),
  internal_name: z.string().default(''),
  is_myself: z.boolean().nullable().default(false),
});
